import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Database, 
  Cloud, 
  Workflow, 
  Calendar, 
  Play, 
  CheckCircle2, 
  AlertCircle,
  Clock,
  TrendingUp,
  ArrowRight,
  Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/services/api";
import { formatDistanceToNow } from "date-fns";

const StatCard = ({ title, value, icon: Icon, description, color = "primary" }) => (
  <Card className="shadow-soft hover:shadow-medium transition-all duration-200">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
          {description && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-${color}/10`}>
          <Icon className={`w-6 h-6 text-${color}`} strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ExecutionRow = ({ execution }) => {
  const statusConfig = {
    completed: { icon: CheckCircle2, color: "text-green-600", bg: "bg-green-50" },
    failed: { icon: AlertCircle, color: "text-red-600", bg: "bg-red-50" },
    running: { icon: Clock, color: "text-blue-600", bg: "bg-blue-50" },
  };
  
  const config = statusConfig[execution.status] || statusConfig.running;
  const StatusIcon = config.icon;

  return (
    <div 
      className="flex items-center justify-between py-3 px-4 rounded-lg hover:bg-muted/50 transition-colors"
      data-testid={`execution-row-${execution.id}`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${config.bg}`}>
          <StatusIcon className={`w-4 h-4 ${config.color}`} />
        </div>
        <div>
          <p className="font-medium text-sm">{execution.pipeline_name}</p>
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(execution.started_at), { addSuffix: true })}
          </p>
        </div>
      </div>
      <Badge variant="outline" className="capitalize">
        {execution.status}
      </Badge>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState({
    connections: { oracle: 0, bigquery: 0 },
    pipelines: 0,
    active_schedules: 0,
    total_runs: 0,
    success_rate: 0,
    recent_executions: [],
    cdc: { total_streams: 0, active_streams: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const data = await api.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Oracle Connections"
          value={stats.connections.oracle}
          icon={Database}
          description="Active source connections"
        />
        <StatCard
          title="BigQuery Destinations"
          value={stats.connections.bigquery}
          icon={Cloud}
          description="Configured destinations"
        />
        <StatCard
          title="Pipelines"
          value={stats.pipelines}
          icon={Workflow}
          description={`${stats.active_schedules} scheduled`}
        />
        <StatCard
          title="CDC Streams"
          value={stats.cdc?.active_streams || 0}
          icon={Zap}
          description={`${stats.cdc?.total_streams || 0} total streams`}
        />
        <StatCard
          title="Success Rate"
          value={`${stats.success_rate}%`}
          icon={TrendingUp}
          description={`${stats.total_runs} total runs`}
        />
      </div>

      {/* Quick Actions & Recent Executions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/connections" data-testid="quick-action-connections">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <Database className="w-5 h-5" />
                Configure Connections
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/pipelines/new" data-testid="quick-action-new-pipeline">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <Workflow className="w-5 h-5" />
                Create New Pipeline
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/cdc" data-testid="quick-action-cdc">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <Zap className="w-5 h-5" />
                Setup CDC Streams
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
            <Link to="/tables" data-testid="quick-action-browse-tables">
              <Button variant="outline" className="w-full justify-start gap-3 h-12">
                <Play className="w-5 h-5" />
                Browse Oracle Tables
                <ArrowRight className="w-4 h-4 ml-auto" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Executions */}
        <Card className="lg:col-span-2 shadow-soft">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Executions</CardTitle>
            <Link to="/history">
              <Button variant="ghost" size="sm" data-testid="view-all-history">
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {stats.recent_executions.length === 0 ? (
              <div className="empty-state py-8">
                <Clock className="empty-state-icon" />
                <p className="text-muted-foreground">No executions yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Create and run a pipeline to see execution history
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {stats.recent_executions.slice(0, 5).map((exec) => (
                  <ExecutionRow key={exec.id} execution={exec} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Getting Started */}
      {stats.pipelines === 0 && (
        <Card className="shadow-soft border-dashed border-2">
          <CardContent className="p-8">
            <div className="text-center max-w-lg mx-auto">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Workflow className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Started with UltiDataLake</h3>
              <p className="text-muted-foreground mb-6">
                Build your first data lake pipeline in minutes. Connect your Oracle database, 
                select tables, and configure transformations for Bronze, Silver, and Gold stages.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/connections">
                  <Button variant="outline" data-testid="get-started-connections">
                    <Database className="w-4 h-4 mr-2" />
                    Setup Connections
                  </Button>
                </Link>
                <Link to="/pipelines/new">
                  <Button data-testid="get-started-pipeline">
                    <Workflow className="w-4 h-4 mr-2" />
                    Create Pipeline
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
